import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Sparkles, Lock, X, Mail, ArrowRight } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export const UpgradeModal = ({ open, onClose, reason = 'cap', isAnonymous = false, trySessionId = null }) => {
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const onSaveEmail = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSaving(true);
    try {
      await api.post('/email/capture', {
        email,
        context: `anon_cap_${reason}`,
        try_session_id: trySessionId || null,
      });
      toast.success('We saved your spot. Check your inbox.');
      setEmail('');
    } catch {
      toast.error('Could not save email. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] grid place-items-center p-4"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ y: 20, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.97 }}
            transition={{ type: 'spring', damping: 22, stiffness: 220 }}
            className="relative w-full max-w-lg card-base p-7 overflow-hidden"
            data-testid="upgrade-modal"
          >
            <button onClick={onClose} className="absolute top-3 right-3 btn-ghost p-1.5 rounded">
              <X className="h-4 w-4" />
            </button>
            <div className="absolute -right-12 -top-16 h-56 w-56 rounded-full bg-[#FF6B1A]/15 blur-3xl pointer-events-none" />
            <div className="absolute -left-10 -bottom-16 h-44 w-44 rounded-full bg-[#39FF14]/10 blur-3xl pointer-events-none" />

            <div className="relative">
              <div className="h-11 w-11 rounded-xl grid place-items-center bg-gradient-to-br from-[#FF6B1A] to-[#FF8A4A] mb-4">
                <Lock className="h-5 w-5 text-white" />
              </div>
              <span className="text-[10px] uppercase tracking-widest text-[#39FF14]">// you hit the limit</span>
              <h3 className="display text-2xl font-semibold mt-1 leading-tight">
                {isAnonymous
                  ? "You're hooked. Sign up to keep your agent."
                  : 'Upgrade to Pro for unlimited messages.'}
              </h3>
              <p className="text-sm text-white/65 mt-2">
                {isAnonymous
                  ? 'Save your conversation, build custom agents, and never run out.'
                  : 'You’ve used your monthly free quota. Pro unlocks unlimited chat, unlimited custom agents, and multi-model routing.'}
              </p>

              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                {isAnonymous ? (
                  <>
                    <Link to="/login" onClick={onClose} className="btn-primary inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-medium flex-1">
                      Sign up free <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link to="/pricing" onClick={onClose} className="btn-secondary inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm flex-1">
                      <Sparkles className="h-4 w-4" /> See Pro
                    </Link>
                  </>
                ) : (
                  <Link to="/pricing" onClick={onClose} className="btn-primary inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-medium flex-1">
                    Upgrade to Pro <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </div>

              {isAnonymous && (
                <form onSubmit={onSaveEmail} className="mt-5 hairline rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-widest text-white/45 mb-2">// or save your spot</div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        required
                        className="input pl-9"
                        data-testid="upgrade-modal-email"
                      />
                    </div>
                    <button type="submit" disabled={saving} className="btn-secondary rounded-xl px-4 py-2 text-sm whitespace-nowrap disabled:opacity-50">
                      {saving ? 'Saving…' : 'Notify me'}
                    </button>
                  </div>
                  <p className="text-[10px] text-white/40 mt-2">No spam. We'll send you your transcript + a 14-day Pro trial when you sign up.</p>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
